import React, {useEffect, useState} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Dialog from "../../../base/dialog";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Col, Row} from "reactstrap";
import Form from "../../../base/form";
import {OutlinedInputForm} from "../../../base/input/outlined_input";
import Button from "../../../base/button/button";
import {deepEqual} from "../../../../../core/services/utils/utils";


const schema = Yup.object().shape({
    name: Yup.string().nullable().trim().required(ValidateMessages.required),
    description: Yup.string().trim().nullable(),
});


const validate = makeValidate(schema);
const required = makeRequired(schema);


const EditProductTechnicalFileInformationDialog = ({open, close, technicalFile, update}) => {
    const [submitting, setSubmitting] = useState(false);
    const [initialValues, setInitialValues] = useState({});
    const isMounted = useIsMounted();

    /**
     * With each change in technical file:
     * - sets the new values in initial values of the form.
     */
    useEffect(() => {
        setInitialValues({
            name: technicalFile?.name,
            description: technicalFile?.description,
        });
    }, [technicalFile])

    /**
     * Updates the information of the given technical file.
     *
     * @param {any} values form values
     * @return {Promise<void>}
     */
    const updateDocument = async (values) => {
        const forApi = {
            name: values.name?.trim(),
            description: values.description?.trim(),
        }
        if (deepEqual(initialValues, forApi)) {
            close();
            return;
        }
        setSubmitting(true);
        const response = await update(technicalFile, forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            close();
        }
        setSubmitting(false);
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => close()}
                maxWidth={'sm'}
                loading={submitting}
                disableEscapeKeyDown={submitting}
                disableBackdropClick={submitting}
                className={'bound-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Edit Document
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-2 pt-4'}>
                    <Col xs={12} className={'mb-4'}>
                        <Form
                            className='d-flex flex-wrap w-100 align-items-start'
                            onSubmit={updateDocument}
                            id={'form-id'}
                            validate={validate}
                            initialValues={initialValues}
                            render={({values, form}) => {
                                return (
                                    <>
                                        <Col xs={12} className={'p-0 mb-3'}>
                                            <OutlinedInputForm
                                                fullWidth
                                                name={'name'}
                                                placeholder={'title...'}
                                                label={"Title"}
                                                className={'mr-2'}
                                                required={required.name}
                                                value={values.name ?? ''}
                                                onChange={(e) => form.change(e.target.name, e.target.value)}
                                            />
                                        </Col>
                                        <Col xs={12} className={'p-0 mb-3'}>
                                            <OutlinedInputForm
                                                fullWidth
                                                name={'description'}
                                                placeholder={'description...'}
                                                label={"Description"}
                                                multiline
                                                minRows={8}
                                                className={'mr-2'}
                                                required={required.description}
                                                value={values.description ?? ''}
                                                onChange={(e) => form.change(e.target.name, e.target.value)}
                                            />
                                        </Col>
                                    </>
                                )
                            }}
                        />
                    </Col>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={!open}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => close()}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Close
                            </Button>
                            <Button
                                disabled={!open}
                                variant={'dialog'}
                                color={'primary'}
                                type={'submit'}
                                form={'form-id'}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {
                                    submitting
                                        ? "Submitting..."
                                        : "Submit"
                                }
                            </Button>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default EditProductTechnicalFileInformationDialog;
